import React, { useContext, useEffect, useState } from 'react'
import { UpdateDemoTradingAccount } from 'api/Clients/Types/DemoAccountDetails'
import { WelcomeAccountDetails } from 'api/Clients/Types/WelcomeAccountDetails'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { Loading } from 'components/Loading'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchAccountGroupByWelcomeAccount,
  fetchTradingAccountsLeverageTypesAction,
  fetchTradingAccountStatusAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { isEmpty } from 'utils/is-empty'

import { TradingAccountTab } from '../Navigation/TradingAccountNavigation'
import { WelcomeTradingAccountNavigation } from '../Navigation/WelcomeTradingAccountNavigation'
import {
  AccountFundsType,
  DemoWelcomeAccountDetailsForm,
} from './Form/DemoWelcomeAccountDetailsForm'
import {
  FundsTradingDemoWelcomeAccountForm,
  FundsTradingDemoWelcomeAccountFormValues,
} from './Form/FundsTradingDemoWelcomeAccountForm'
import TradingAccountDetailsPage, { TradingAccountDetailsProps } from './TradingAccountDetailsPage'

interface Props extends TradingAccountDetailsProps {
  data: WelcomeAccountDetails
  forceRefresh(): void
}

const WelcomeAccountDetailsPage: React.FC<Props> = ({
  data,
  isEdit,
  setEdit,
  forceRefresh,
  ...props
}) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const dispatch = useAppDispatch()

  const setToast = useContext(ToastContext)
  const [leverageModalConfirmation, setLeverageModalConfirmation] = useState<{
    visible: boolean
    values?: UpdateDemoTradingAccount
  }>({ visible: false })
  const [accountFundsTypeModal, setAccountFundsTypeModal] = useState<AccountFundsType>()

  const tradingAccountStatus = useAppSelector((state) => state.tradingAccounts.tradingAccountStatus)
  const leverageType = useAppSelector((state) => state.tradingAccounts.leverages)
  const accountGroupsByWelcomeAccount = useAppSelector(
    (state) => state.tradingAccounts.accountGroupsByWelcomeAccount
  )

  useEffect(() => {
    if (data) {
      dispatch(fetchAccountGroupByWelcomeAccount(tradingAccountsApiClient, data.id))
      if (isEmpty(leverageType)) {
        dispatch(fetchTradingAccountsLeverageTypesAction(tradingAccountsApiClient))
      }
      if (isEmpty(tradingAccountStatus)) {
        dispatch(fetchTradingAccountStatusAction(tradingAccountsApiClient))
      }
    }
  }, [data.id, data]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenAddDeductFunds = async (fundsType: AccountFundsType) => {
    setAccountFundsTypeModal(fundsType)
  }

  const onSubmitWithMaybeConfirmation = async (values: UpdateDemoTradingAccount) => {
    const isChangingLeverage = data.leverage.id !== values.leverageTypeId
    if (isChangingLeverage) {
      const isLeverageEnabled = leverageType.find(
        (l) => l.id === Number(values.leverageTypeId)
      )?.enabled
      if (!isLeverageEnabled) {
        return setLeverageModalConfirmation({ visible: true, values })
      } else {
        await onSubmit(values)
      }
    } else {
      await onSubmit(values)
    }
  }

  const onSubmit = async (values: UpdateDemoTradingAccount) => {
    if (data) {
      try {
        await tradingAccountsApiClient.updateWelcomeAccount(data.id, values)
        setToast(successToast('Welcome Account updated.'))
        forceRefresh()
      } catch (e) {
        console.error(e)
        setLeverageModalConfirmation({ visible: false, values: undefined })
        forceRefresh()
      }
    }
  }

  const handleSubmitDeductFunds = async (values: FundsTradingDemoWelcomeAccountFormValues) => {
    const req: FundsTradingDemoWelcomeAccountFormValues = {
      ...values,
      tradingAccountId: data.id,
    }
    await tradingAccountsApiClient.depositWithdrawalTradingAccount(
      'welcome-accounts',
      AccountFundsType.DeductFunds,
      req
    )
    setToast(successToast('Funds withdrawn successfully.'))
    setAccountFundsTypeModal(undefined)
  }

  const handleDisqualify = async () => {
    await tradingAccountsApiClient.forceCloseTradesForWelcomeAccount(data.id)
    forceRefresh()
  }

  return (
    <React.Fragment>
      {leverageModalConfirmation.visible && leverageModalConfirmation.values && (
        <ConfirmationModal
          contentText={`You have requested to change the Leverage to an option we no longer offer in Client Area/Website.
                        Please confirm your action.`}
          yesButtonText='Proceed'
          onYes={() => onSubmit(leverageModalConfirmation.values as UpdateDemoTradingAccount)}
          onCancel={() =>
            setLeverageModalConfirmation({
              visible: false,
              values: undefined,
            })
          }
          visible={leverageModalConfirmation.visible}
        />
      )}
      {accountFundsTypeModal && (
        <Modal
          visible={!!accountFundsTypeModal}
          width={1100}
          onClose={() => setAccountFundsTypeModal(undefined)}
        >
          <FundsTradingDemoWelcomeAccountForm
            fundType={accountFundsTypeModal}
            data={data}
            onSubmit={handleSubmitDeductFunds}
          />
        </Modal>
      )}
      <WelcomeTradingAccountNavigation
        data={data}
        hideTabs={data.accountGroup.platformType.id === 3}
        isEdit={isEdit}
        tab={TradingAccountTab.Details}
      />
      <Loading isLoading={!data}>
        {data && (
          <React.Fragment>
            <DemoWelcomeAccountDetailsForm
              {...props}
              data={data}
              isEdit={isEdit}
              setEdit={setEdit}
              onSubmit={onSubmitWithMaybeConfirmation}
              onDisqualify={handleDisqualify}
              onOpenAddDeductFunds={handleOpenAddDeductFunds}
              accountGroups={accountGroupsByWelcomeAccount}
              leverageType={leverageType}
              canEditTradingAccount={PermissionsEnum.CanEditWelcomeTradingAccount}
              canResetAccountPassword={PermissionsEnum.CanResetWelcomeAccountPassword}
            />
          </React.Fragment>
        )}
      </Loading>
    </React.Fragment>
  )
}

interface WelcomeTradingAccountDetailsProps {
  data: WelcomeAccountDetails
  forceRefresh(): void
}

const WelcomeTradingAccountDetailsPageComponent: React.FC<WelcomeTradingAccountDetailsProps> = (
  props
) => {
  const { data, forceRefresh } = props
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  return (
    <TradingAccountDetailsPage
      data={data}
      type='welcomeAccount'
      resetTradingAccountPassword={tradingAccountsApiClient.resetWelcomeTradingAccountPassword}
      render={(props) => (
        <WelcomeAccountDetailsPage {...props} data={data} forceRefresh={forceRefresh} />
      )}
    />
  )
}

export default WelcomeTradingAccountDetailsPageComponent
